import type {
  DeckInventoryMatch,
  DeckProfile,
  DeckScore,
  DeckSection,
} from "@/types/deckBuilder";

export interface DeckScoringInput {
  matches: DeckInventoryMatch[];
  profile: DeckProfile;
  /**
   * Theme target — when supplied, theme score rewards matched cards whose
   * normalized name or type tag includes the target string.
   */
  themeKeyword?: string;
  /**
   * Optional fraction of inventory consumed by this deck (0–1) — used by the
   * inventory-fit score to penalize decks that consume too much of the seller's
   * remaining inventory.
   */
  inventoryUtilization?: number;
  /**
   * Estimated profit / target sale price — used by the value score to reward
   * products with healthy margins.
   */
  expectedMarginPct?: number;
}

const WEIGHT_COMPLETENESS = 0.35;
const WEIGHT_PLAYABILITY = 0.25;
const WEIGHT_THEME = 0.15;
const WEIGHT_INVENTORY_FIT = 0.15;
const WEIGHT_VALUE = 0.1;

export function scoreDeck(input: DeckScoringInput): DeckScore {
  const completenessScore = scoreCompleteness(input.matches, input.profile);
  const { score: playabilityScore, warnings: playWarnings } = scorePlayability(
    input.matches,
    input.profile,
  );
  const themeScore = scoreTheme(input.matches, input.themeKeyword);
  const inventoryFitScore = scoreInventoryFit(input.inventoryUtilization);
  const valueScore = scoreValue(input.expectedMarginPct);

  const finalScore = clamp(
    Math.round(
      completenessScore * WEIGHT_COMPLETENESS +
        playabilityScore * WEIGHT_PLAYABILITY +
        themeScore * WEIGHT_THEME +
        inventoryFitScore * WEIGHT_INVENTORY_FIT +
        valueScore * WEIGHT_VALUE,
    ),
    0,
    100,
  );

  const warnings: string[] = [...playWarnings];
  const totalRequested = input.matches.reduce((s, m) => s + m.requestedQty, 0);
  const totalIncluded = input.matches.reduce((s, m) => s + m.ownedQty, 0);
  if (
    totalIncluded < input.profile.targetCards &&
    totalIncluded < totalRequested
  ) {
    warnings.push(
      `Only ${totalIncluded} of ${input.profile.targetCards} target cards available — recommended as Deck Core or Upgrade Kit.`,
    );
  }

  return {
    completenessScore,
    playabilityScore,
    themeScore,
    inventoryFitScore,
    valueScore,
    finalScore,
    warnings,
  };
}

// ─── Component scorers ────────────────────────────────────────────────────────

function scoreCompleteness(
  matches: DeckInventoryMatch[],
  profile: DeckProfile,
): number {
  if (matches.length === 0) return 0;
  const totalRequested = matches.reduce((s, m) => s + m.requestedQty, 0);
  const totalOwned = matches.reduce((s, m) => s + m.ownedQty, 0);
  if (totalRequested === 0) return 0;
  const fillRate = totalOwned / totalRequested;
  // Reward decks close to the target card count.
  const targetFraction = Math.min(1, totalOwned / profile.targetCards);
  const blended = fillRate * 0.7 + targetFraction * 0.3;
  return Math.round(blended * 100);
}

function scorePlayability(
  matches: DeckInventoryMatch[],
  profile: DeckProfile,
): { score: number; warnings: string[] } {
  const warnings: string[] = [];
  const counts = countBySection(matches);
  const total =
    counts.Pokemon + counts.Trainer + counts.Energy + counts.Unknown;
  if (total === 0) return { score: 0, warnings };

  let score = 50;

  // ── Pokémon range ──
  if (counts.Pokemon < profile.pokemonMin) {
    score -= 10;
    warnings.push(
      `Only ${counts.Pokemon} Pokémon — minimum ${profile.pokemonMin} for this profile.`,
    );
  } else if (counts.Pokemon > profile.pokemonMax) {
    score -= 5;
    warnings.push(
      `${counts.Pokemon} Pokémon exceeds the maximum of ${profile.pokemonMax}.`,
    );
  } else {
    score += 15;
  }

  // ── Trainer range ──
  if (counts.Trainer < profile.trainerMin) {
    score -= 8;
    warnings.push(
      `Only ${counts.Trainer} Trainer cards — minimum ${profile.trainerMin}.`,
    );
  } else if (counts.Trainer > profile.trainerMax) {
    score -= 3;
    warnings.push(
      `${counts.Trainer} Trainer cards exceeds the maximum of ${profile.trainerMax}.`,
    );
  } else {
    score += 10;
  }

  // ── Energy range ──
  if (counts.Energy < profile.energyMin) {
    score -= 5;
    warnings.push(
      `Only ${counts.Energy} Energy cards — minimum ${profile.energyMin}.`,
    );
  } else if (counts.Energy > profile.energyMax) {
    score -= 5;
    warnings.push(
      `${counts.Energy} Energy cards exceeds the maximum of ${profile.energyMax}.`,
    );
  } else {
    score += 5;
  }

  // ── Non-basic copies > 4 ──
  for (const match of matches) {
    const isBasicEnergy = looksLikeBasicEnergy(match.requested.name);
    if (!isBasicEnergy && match.ownedQty > profile.maxCopiesPerCard) {
      score -= 5;
      warnings.push(
        `${match.requested.name} included ${match.ownedQty} copies — non-basic-energy max is ${profile.maxCopiesPerCard}.`,
      );
    }
  }

  return { score: clamp(score, 0, 100), warnings };
}

function scoreTheme(
  matches: DeckInventoryMatch[],
  themeKeyword: string | undefined,
): number {
  if (!themeKeyword) return 50;
  const target = themeKeyword.toLowerCase();
  let total = 0;
  let hits = 0;
  for (const match of matches) {
    for (const card of match.matchedCards) {
      total += 1;
      const haystack = [
        card.name,
        ...(card.tcgTypes ?? []),
        card.tcgSeries ?? "",
      ]
        .join(" ")
        .toLowerCase();
      if (haystack.includes(target)) hits += 1;
    }
  }
  if (total === 0) return 0;
  return Math.round((hits / total) * 100);
}

function scoreInventoryFit(utilization: number | undefined): number {
  if (utilization === undefined) return 60;
  // Sweet spot is ~10–40% utilization. Excess above 60% penalizes since the
  // deck consumes too much of the remaining catalog.
  if (utilization < 0.05) return 40;
  if (utilization < 0.4) return 90;
  if (utilization < 0.6) return 70;
  return 40;
}

function scoreValue(marginPct: number | undefined): number {
  if (marginPct === undefined) return 50;
  if (marginPct >= 0.5) return 100;
  if (marginPct >= 0.3) return 80;
  if (marginPct >= 0.15) return 60;
  if (marginPct >= 0) return 40;
  return 0;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function countBySection(
  matches: DeckInventoryMatch[],
): Record<DeckSection, number> {
  const out: Record<DeckSection, number> = {
    Pokemon: 0,
    Trainer: 0,
    Energy: 0,
    Unknown: 0,
  };
  for (const match of matches) {
    const section = match.requested.section ?? "Unknown";
    out[section] += match.ownedQty;
  }
  return out;
}

function looksLikeBasicEnergy(name: string): boolean {
  const lower = name.toLowerCase();
  if (!lower.includes("energy")) return false;
  return /(fire|water|grass|lightning|psychic|fighting|darkness|metal|fairy|dragon)\s+energy/.test(
    lower,
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
